import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { MapPin, Clock, DollarSign, User, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [liability, setLiability] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState("");
  const [msgError, setMsgError] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setP(data);
  }, [id]);
  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/messages`);
      setMessages(data);
      setMsgError(false);
    } catch {
      setMsgError(true);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user) loadMessages(); }, [user, loadMessages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgBody.trim()) return;
    try {
      await api.post(`/projects/${id}/messages`, { body: msgBody });
      setMsgBody("");
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    }
  };

  const submitBid = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Login as a contractor to bid"); return; }
    if (user.role !== "contractor") { toast.error("Only contractors can bid"); return; }
    if (!user.subscription_active) { toast.error("Subscribe to submit bids"); return; }
    if (!liability) { toast.error("Please accept the liability terms"); return; }
    try {
      await api.post("/bids", { project_id: id, amount: parseFloat(amount), message, liability_accepted: true });
      toast.success("Bid submitted");
      setAmount(""); setMessage(""); setLiability(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit bid");
    }
  };

  if (!p) return <div className="max-w-4xl mx-auto px-5 py-20 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-10">
      <div className="bg-white border border-[#E2DCD0] rounded-xl p-8">
        <span className="px-2.5 py-1 bg-[#EAE5D9] rounded-full text-[10px] uppercase tracking-[0.15em] font-bold text-[#314A3E]">{p.category}</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-4" style={{ fontFamily: "Outfit" }}>{p.title}</h1>
        <div className="flex flex-wrap items-center gap-5 mt-4 text-sm text-stone-600">
          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {p.owner_name}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.location}</span>
          {p.budget && <span className="flex items-center gap-1 font-semibold text-[#314A3E]"><DollarSign className="w-3.5 h-3.5" /> ${p.budget}</span>}
          {p.timeline && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.timeline}</span>}
        </div>
        <p className="mt-6 text-stone-700 leading-relaxed whitespace-pre-line">{p.description}</p>
      </div>

      {/* Bids */}
      <div className="mt-10">
        <h2 className="text-2xl font-bold tracking-tight mb-5" style={{ fontFamily: "Outfit" }}>Bids ({p.bids?.length || 0})</h2>
        {p.bids?.length > 0 ? (
          <div className="space-y-3 mb-8">
            {p.bids.map(b => (
              <div key={b.bid_id} className="bg-white border border-[#E2DCD0] rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">{b.contractor_name}</div>
                  <div className="text-[#B85C47] font-bold">${b.amount}</div>
                </div>
                <p className="text-sm text-stone-700">{b.message}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-stone-500 mb-6">No bids yet.</p>}

        {user?.role === "contractor" && (
          <form onSubmit={submitBid} className="bg-white border border-[#E2DCD0] rounded-xl p-5 space-y-3">
            <div className="text-sm font-semibold">Submit a bid</div>
            <Input data-testid="bid-amount-input" type="number" step="0.01" placeholder="Your price ($)" value={amount} onChange={e => setAmount(e.target.value)} required />
            <Textarea data-testid="bid-message-input" placeholder="Tell the homeowner why you're a great fit…" value={message} onChange={e => setMessage(e.target.value)} required />
            <label className="flex items-start gap-2 text-xs text-stone-700">
              <input data-testid="bid-liability-checkbox" type="checkbox" checked={liability} onChange={e => setLiability(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#314A3E]" />
              <span>I accept full liability for the work I bid on and indemnify Kootenay Home Pros from any claims or damages.</span>
            </label>
            <Button data-testid="submit-bid-btn" type="submit" className="bg-[#B85C47] hover:bg-[#a04e3b] text-white">Send bid</Button>
            {!user.subscription_active && <p className="text-xs text-[#B85C47]">Subscribe to unlock bidding.</p>}
          </form>
        )}
      </div>

      {/* Messages */}
      {user && !msgError && (
        <div className="mt-10">
          <h2 className="text-2xl font-bold tracking-tight mb-5 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <MessageCircle className="w-5 h-5 text-[#314A3E]" /> Conversation
          </h2>
          <div className="bg-white border border-[#E2DCD0] rounded-xl p-5">
            {messages.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">No messages yet. Start the conversation below.</p>
            ) : (
              <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                {messages.map(m => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        mine ? "bg-[#314A3E] text-white" : "bg-[#EAE5D9] text-stone-800"
                      }`}>
                        <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${mine ? "text-[#EAB89F]" : "text-[#B85C47]"}`}>
                          {m.sender_name} · {m.sender_role}
                        </div>
                        <div className="text-sm whitespace-pre-line">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <form onSubmit={sendMessage} className="flex gap-2 pt-3 border-t border-[#E2DCD0]">
              <Input
                data-testid="message-input"
                value={msgBody}
                onChange={e => setMsgBody(e.target.value)}
                placeholder={user.role === "homeowner" ? "Message a contractor…" : "Reach out to the homeowner…"}
                className="flex-1"
              />
              <Button data-testid="send-message-btn" type="submit" className="bg-[#314A3E] hover:bg-[#243a30] text-white">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
